import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Heart, Save, RotateCcw, Share2, Loader2, Lock, Clock, Bookmark } from "lucide-react";
import Envelope from "@/components/Envelope";
import PaperTexture from "@/components/PaperTexture";
import EmotionTheme, { themeById } from "@/components/EmotionTheme";
import Countdown from "@/components/Countdown";
import HeartReactions, { HEART_REACTIONS } from "@/components/HeartReactions";
import { getLetter, trackOpen, trackReplay, trackSave, fileUrl, fetchManage } from "@/lib/api";
import { emotionById } from "@/lib/constants";

export default function Recipient() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const manageToken = params.get("manage");
  const [letter, setLetter] = useState(null);
  const [manage, setManage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("envelope"); // envelope | breaking | opening | letter
  const openStartRef = useRef(null);

  const load = async () => {
    try {
      const data = await getLetter(slug);
      setLetter(data);
    } catch {
      toast.error("This letter could not be found.");
    } finally {
      setLoading(false);
    }
    if (manageToken) {
      try {
        const m = await fetchManage(slug, manageToken);
        setManage(m);
      } catch {
        /* silent */
      }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const openLetter = () => {
    if (stage !== "envelope") return;
    setStage("breaking");
    setTimeout(() => setStage("opening"), 1600);
    setTimeout(() => {
      setStage("letter");
      openStartRef.current = Date.now();
      trackOpen(slug, 0).catch(() => {});
    }, 3300);
  };

  const replay = () => { trackReplay(slug).catch(() => {}); setStage("envelope"); };
  const saveKeepsake = () => { trackSave(slug).catch(() => {}); toast.success("Saved. This letter will live here for you."); };
  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href.split("?")[0]);
      toast.success("Link copied. Share it with someone dear.");
    } catch { toast.error("Could not copy"); }
  };

  useEffect(() => {
    return () => {
      if (openStartRef.current) {
        const s = Math.round((Date.now() - openStartRef.current) / 1000);
        trackOpen(slug, s).catch(() => {});
      }
    };
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen cream-bg flex items-center justify-center text-[#1C1917]">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!letter) {
    return (
      <div className="min-h-screen cream-bg flex flex-col items-center justify-center text-[#1C1917] px-6 text-center">
        <h1 className="font-serif-elegant text-4xl">This letter has drifted away.</h1>
        <p className="mt-3 text-[#4c3f34]">The link may be incorrect or the letter no longer exists.</p>
        <Link to="/" className="mt-8 px-6 py-3 rounded-full bg-[#991B1B] text-white hover:bg-[#7F1D1D]">Return home</Link>
      </div>
    );
  }

  // Locked time capsule
  if (letter.locked) {
    return <LockedView letter={letter} onExpire={load} />;
  }

  const emo = emotionById(letter.emotion);
  const photos = letter.attachments.filter((a) => a.kind === "photo");
  const voice = letter.attachments.find((a) => a.kind === "voice");

  return (
    <EmotionTheme emotionId={letter.emotion}>
      {/* Header */}
      <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-6">
        <Link to="/" className="flex items-center gap-2 text-[#4c3f34] hover:text-[#1C1917] transition-colors" data-testid="recipient-home-link">
          <Heart size={16} className="text-[#991B1B]" fill="#991B1B" />
          <span className="font-serif-elegant text-lg">WrittenHearts</span>
        </Link>
        <div className="text-xs uppercase tracking-[0.35em] text-[#4c3f34]/60">A letter for you</div>
        <div className="w-8" />
      </div>

      {manage && (
        <ManagePanel manage={manage} />
      )}

      <AnimatePresence mode="wait">
        {stage !== "letter" && (
          <motion.section
            key="env"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto px-6 py-10 md:py-20 text-center"
          >
            <p className="uppercase tracking-[0.4em] text-xs text-[#991B1B]">
              {letter.is_time_capsule ? "A message from your past" : "Someone has written from their heart"}
            </p>
            <h1 className="mt-4 font-serif-elegant text-4xl md:text-6xl text-[#1C1917]">
              For {letter.recipient_name || "you"}
            </h1>
            <p className="mt-3 font-hand text-2xl text-[#7F1D1D]">— from {letter.sender_name}</p>

            <div className="mt-14 flex justify-center">
              <Envelope
                sealed
                recipient={letter.recipient_name}
                sender={letter.sender_name}
                accent={emo.accent}
                breaking={stage === "breaking"}
                opened={stage === "opening"}
                stamp={letter.stamp || "red_heart"}
                onSealClick={openLetter}
                pulse={stage === "envelope"}
                dateBadge={letter.is_time_capsule ? new Date(letter.written_on).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null}
                size="lg"
              />
            </div>

            {stage === "envelope" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="mt-12 flex flex-col items-center gap-3"
              >
                <p className="font-hand text-xl text-[#7F1D1D]">tap the wax seal to open</p>
                <button
                  data-testid="recipient-open-btn"
                  onClick={openLetter}
                  className="px-8 py-3 rounded-full bg-[#991B1B] text-white hover:bg-[#7F1D1D] transition-colors shadow-lg shadow-[#991B1B]/25 text-sm"
                >
                  Open My Letter
                </button>
              </motion.div>
            )}
            {stage === "breaking" && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-14 text-[#4c3f34] text-sm italic">
                the wax is breaking into little hearts…
              </motion.p>
            )}
            {stage === "opening" && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-14 text-[#4c3f34] text-sm italic">
                the flap lifts…
              </motion.p>
            )}
          </motion.section>
        )}

        {stage === "letter" && (
          <LetterView
            key="ltr"
            letter={letter}
            emo={emo}
            photos={photos}
            voice={voice}
            slug={slug}
            onSave={saveKeepsake}
            onReplay={replay}
            onShare={share}
          />
        )}
      </AnimatePresence>
    </EmotionTheme>
  );
}

/* ---- Locked view ---- */
function LockedView({ letter, onExpire }) {
  const emo = emotionById(letter.emotion);
  const target = new Date(letter.deliver_at);
  const written = new Date(letter.written_on);

  return (
    <EmotionTheme emotionId={letter.emotion}>
      <div className="max-w-3xl mx-auto px-6 py-12 md:py-20 text-center">
        <div className="flex items-center justify-center gap-2 text-[#991B1B] uppercase tracking-[0.35em] text-xs">
          <Lock size={12} /> A message from your past is waiting for you
        </div>
        <h1 className="mt-4 font-serif-elegant text-4xl md:text-6xl text-[#1C1917]">
          Sealed until
        </h1>
        <div className="mt-3 font-serif-elegant text-2xl md:text-3xl italic text-[#7F1D1D]">
          {target.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
        </div>

        <div className="mt-12 flex justify-center">
          <Envelope
            sealed
            recipient={letter.recipient_name}
            sender={letter.sender_name}
            accent={emo.accent}
            stamp={letter.stamp || "red_heart"}
            dateBadge={target.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
            size="lg"
            pulse
          />
        </div>

        <div className="mt-14">
          <p className="uppercase tracking-[0.4em] text-xs text-[#4c3f34]">Unlocks in</p>
          <div className="mt-4">
            <Countdown target={target} onExpire={onExpire} />
          </div>
        </div>

        <div className="mt-14 max-w-md mx-auto rounded-[6px] bg-[#FDFBF7]/80 backdrop-blur-sm border border-[#1C1917]/10 p-5 text-left">
          <div className="flex items-center gap-2 text-[#991B1B] text-xs uppercase tracking-widest">
            <Bookmark size={12} /> Written on
          </div>
          <div className="mt-1 font-serif-elegant text-xl text-[#1C1917]">
            {written.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
          </div>
          <p className="mt-3 text-sm text-[#4c3f34]">
            This letter cannot be opened early. Bookmark this page — a soft notification will greet you the moment it unlocks.
          </p>
        </div>
      </div>
    </EmotionTheme>
  );
}

/* ---- Letter view (unlocked) ---- */
function LetterView({ letter, emo, photos, voice, slug, onSave, onReplay, onShare }) {
  const written = new Date(letter.written_on);
  const opened = new Date(letter.opened_on || Date.now());
  const words = letter.body.split(/(\s+)/);
  const [reactionSent, setReactionSent] = useState(false);
  const [visible, setVisible] = useState(0);

  // typewriter-style reveal by words
  useEffect(() => {
    if (visible >= words.length) return;
    const id = setTimeout(() => setVisible((v) => v + 1), 50);
    return () => clearTimeout(id);
  }, [visible, words.length]);

  return (
    <motion.section
      initial={{ opacity: 0, y: 60 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1 }}
      className="max-w-3xl mx-auto px-6 pb-24"
    >
      {/* Written on / Opened on badge (mainly for time capsule but useful always) */}
      <div className="mb-6 flex flex-col md:flex-row items-center justify-center gap-3 text-xs md:text-sm text-[#4c3f34]">
        <div className="flex items-center gap-2">
          <span className="uppercase tracking-[0.3em] text-[#991B1B]">Written on</span>
          <span className="font-serif-elegant text-lg text-[#1C1917]">
            {written.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
          </span>
        </div>
        <span className="hidden md:inline text-[#1C1917]/30">·</span>
        <div className="flex items-center gap-2">
          <span className="uppercase tracking-[0.3em] text-[#991B1B]">Opened on</span>
          <span className="font-serif-elegant text-lg text-[#1C1917]">
            {opened.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
          </span>
        </div>
      </div>

      <PaperTexture className="p-8 md:p-16">
        <div className="text-center">
          <div className="font-hand text-2xl md:text-3xl" style={{ color: emo.ink }}>
            Dear {letter.recipient_name},
          </div>
        </div>
        <div
          className="mt-8 font-serif-elegant leading-relaxed whitespace-pre-wrap text-xl md:text-2xl"
          style={{ color: emo.ink }}
          data-testid="letter-body"
        >
          {words.slice(0, visible).map((w, i) => (
            <span key={i}>{w}</span>
          ))}
          {visible < words.length && (
            <span
              className="inline-block w-[2px] h-6 align-middle ml-0.5"
              style={{ background: emo.ink, animation: "blink 0.9s steps(1) infinite" }}
            />
          )}
        </div>

        <div className="mt-12 text-right">
          <div className="font-hand text-3xl" style={{ color: emo.ink }}>With love,</div>
          <div className="font-hand text-4xl mt-2" style={{ color: emo.ink }}>{letter.sender_name}</div>
          <div className="mt-3 inline-block">
            <Heart size={16} className="inline text-[#991B1B]" fill="#991B1B" />
          </div>
        </div>

        {photos.length > 0 && (
          <div className="mt-14 border-t border-[#1C1917]/10 pt-10">
            <p className="text-center text-[#4c3f34] text-sm uppercase tracking-[0.3em] mb-6">Tucked inside</p>
            <div className="flex flex-wrap gap-6 justify-center">
              {photos.map((p, i) => (
                <motion.div
                  key={p.file_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0, rotate: (i % 2 === 0 ? -3 : 3) }}
                  transition={{ delay: 0.3 + i * 0.15, duration: 0.6 }}
                  className="bg-white p-3 pb-8 shadow-[0_20px_35px_-15px_rgba(120,80,40,0.35)]"
                  style={{ maxWidth: 240 }}
                  data-testid={`letter-photo-${i}`}
                >
                  <img src={fileUrl(p.file_id)} alt="memory" className="w-full h-40 object-cover" />
                  <p className="mt-3 text-center font-hand text-lg text-[#1C1917]/70">a memory</p>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {voice && (
          <div className="mt-14 border-t border-[#1C1917]/10 pt-10">
            <p className="text-center text-[#4c3f34] text-sm uppercase tracking-[0.3em] mb-6">Hear my voice</p>
            <div className="flex justify-center">
              <audio data-testid="letter-voice" controls src={fileUrl(voice.file_id)} className="w-full max-w-md" />
            </div>
          </div>
        )}
      </PaperTexture>

      {/* Heart Reactions — only for regular letters (not self time-capsules) */}
      {!letter.is_time_capsule && (
        <HeartReactions slug={slug} onReacted={() => setReactionSent(true)} />
      )}

      <div className="mt-14 text-center">
        <p className="font-hand text-2xl text-[#7F1D1D]">Keep this letter forever.</p>
        <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
          <button data-testid="letter-save-btn" onClick={onSave} className="px-5 py-2.5 rounded-full bg-[#1C1917] text-[#FDFBF7] hover:bg-[#3f342c] transition-colors inline-flex items-center gap-2 text-sm">
            <Save size={14} /> Save Letter
          </button>
          <button data-testid="letter-replay-btn" onClick={onReplay} className="px-5 py-2.5 rounded-full border border-[#1C1917]/20 hover:bg-[#1C1917]/5 text-[#1C1917] inline-flex items-center gap-2 text-sm">
            <RotateCcw size={14} /> Replay Opening
          </button>
          <button data-testid="letter-share-btn" onClick={onShare} className="px-5 py-2.5 rounded-full border border-[#1C1917]/20 hover:bg-[#1C1917]/5 text-[#1C1917] inline-flex items-center gap-2 text-sm">
            <Share2 size={14} /> Share This Memory
          </button>
        </div>
        <p className="mt-10 text-[#4c3f34]/70 text-xs">
          Created with <span className="font-serif-elegant">WrittenHearts</span> — Not a message. A moment.
        </p>
      </div>

      <style>{`@keyframes blink { 50% { opacity: 0; } }`}</style>
    </motion.section>
  );
}

/* ---- Sender's private "manage" panel showing reactions received ---- */
function ManagePanel({ manage }) {
  const total = Object.values(manage.reactions || {}).reduce((s, n) => s + n, 0);
  return (
    <div className="max-w-3xl mx-auto px-6 pt-4" data-testid="manage-panel">
      <div className="rounded-[6px] bg-[#FDFBF7]/85 backdrop-blur-sm border border-[#B8935A]/30 p-5">
        <div className="flex items-center gap-2 text-[#B8935A] uppercase tracking-widest text-xs">
          <Bookmark size={12} /> Your private view — only you see this
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm text-[#4c3f34]">
              {manage.opened
                ? `${manage.recipient_name || "They"} opened this on ${new Date(manage.first_opened_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}`
                : `${manage.recipient_name || "They"} has not opened this yet.`}
            </div>
            <div className="text-xs text-[#4c3f34]/70 mt-1">
              {manage.share_count} shares · {manage.open_count} opens · {manage.save_count} saves · {total} reactions
            </div>
          </div>
        </div>

        {total > 0 && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {HEART_REACTIONS.map((r) => {
              const c = manage.reactions?.[r.id] || 0;
              if (c === 0) return null;
              return (
                <div key={r.id} className="flex items-center gap-3 rounded-[4px] bg-[#F5EEDD] px-3 py-2">
                  <span className="text-xl" style={{ color: r.accent }}>{r.emoji}</span>
                  <span className="text-sm text-[#1C1917] flex-1">{r.label}</span>
                  <span className="font-serif-elegant text-lg text-[#991B1B]">{c}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
